import {
  createClient,
  type SupabaseClient,
  type SupabaseClientOptions,
} from '@supabase/supabase-js';
import type { Database } from './types';
import { getSupabaseBaseUrl, getSupabasePublishableKey } from '@/lib/selfHostedUrls';

export type AuthPortal = 'hire' | 'seek' | 'admin';
export type BrowserClientKind = AuthPortal | 'anonymous';

const FIXED_STORAGE_KEYS: Record<Exclude<BrowserClientKind, 'hire'>, string> = {
  seek: 'sb-seek-auth-token',
  admin: 'sb-admin-auth-token',
  anonymous: 'sb-anon-auth-token',
};

type SupportedSchema = keyof Database & string;
type GovernedClientOptions = SupabaseClientOptions<SupportedSchema>;
type AuthStorage = NonNullable<NonNullable<GovernedClientOptions['auth']>['storage']>;

function createMemoryStorage() {
  const values = new Map<string, string>();

  return {
    getItem(key: string) {
      return values.get(key) ?? null;
    },
    setItem(key: string, value: string) {
      values.set(key, value);
    },
    removeItem(key: string) {
      values.delete(key);
    },
  };
}

/**
 * Supabase derives its default browser storage key from the first hostname
 * label. Keeping that derivation explicit preserves existing ForkHire sessions
 * while making every portal's PKCE verifier location reviewable.
 */
export function getPortalAuthStorageKey(portal: BrowserClientKind, supabaseUrl: string) {
  if (portal !== 'hire') return FIXED_STORAGE_KEYS[portal];

  const projectReference = new URL(supabaseUrl).hostname.split('.')[0];
  if (!projectReference) {
    throw new Error('supabase_auth_storage_key_unavailable');
  }

  return `sb-${projectReference}-auth-token`;
}

export function getPortalPkceVerifierStorageKey(portal: AuthPortal, supabaseUrl: string) {
  return `${getPortalAuthStorageKey(portal, supabaseUrl)}-code-verifier`;
}

/**
 * Keep durable portal sessions in localStorage while confining the one-time
 * PKCE verifier to the initiating tab. Auth-js uses exactly one storage key
 * ending in `-code-verifier`; every other key remains on the established
 * durable session store.
 */
export function createPortalBrowserAuthStorage(
  portal: AuthPortal,
  supabaseUrl: string,
  durableStorage: AuthStorage = localStorage,
  transactionStorage: AuthStorage = sessionStorage,
): AuthStorage {
  const verifierKey = getPortalPkceVerifierStorageKey(portal, supabaseUrl);
  const storageFor = (key: string) => key === verifierKey ? transactionStorage : durableStorage;

  return {
    getItem(key: string) {
      return storageFor(key).getItem(key);
    },
    setItem(key: string, value: string) {
      return storageFor(key).setItem(key, value);
    },
    removeItem(key: string) {
      return storageFor(key).removeItem(key);
    },
  };
}

export function getGovernedBrowserAuthOptions(
  portal: BrowserClientKind,
  supabaseUrl: string,
): NonNullable<GovernedClientOptions['auth']> {
  const persistent = portal !== 'anonymous';

  return {
    ...(persistent && {
      storage: createPortalBrowserAuthStorage(portal, supabaseUrl),
    }),
    storageKey: getPortalAuthStorageKey(portal, supabaseUrl),
    persistSession: persistent,
    autoRefreshToken: persistent,
    detectSessionInUrl: false,
    flowType: 'pkce',
  };
}

export function createGovernedBrowserClient(
  portal: BrowserClientKind,
  supabaseUrl: string,
  publishableKey: string,
): SupabaseClient<Database> {
  return createClient<Database>(supabaseUrl, publishableKey, {
    auth: getGovernedBrowserAuthOptions(portal, supabaseUrl),
  });
}

/**
 * Creates a deliberately non-persistent client for one-time token-hash flows.
 * Each invocation owns a fresh in-memory store, so it cannot inherit or replace
 * the durable Hire, Seek, or Admin browser session.
 */
export function createEphemeralPortalClient(
  portal: AuthPortal,
  supabaseUrl = getSupabaseBaseUrl(),
  publishableKey = getSupabasePublishableKey(),
): SupabaseClient<Database> {
  return createClient<Database>(supabaseUrl, publishableKey, {
    auth: {
      storage: createMemoryStorage(),
      storageKey: `${getPortalAuthStorageKey(portal, supabaseUrl)}-ephemeral`,
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
      flowType: 'pkce',
    },
  });
}
